import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { CircularProgress } from '../CircularProgress';
import { KPICell, SectionFrame, type PeriodContentProps } from './shared';

const PALETTE = {
  firm:     '#0f766e',  // teal — committed window
  forecast: '#3b82f6',  // blue — beyond firm
  ringDark: '#1e293b',
};

export function Forecast({ data, formatCurrency }: PeriodContentProps) {
  const { firm, forecast, default_firm_days } = data;
  const totalSchedules = firm.schedules + forecast.schedules;
  const totalQty       = firm.total_qty + forecast.total_qty;
  const totalOpenQty   = firm.open_qty + forecast.open_qty;
  const totalValue     = firm.total_value + forecast.total_value;
  const totalOpenValue = firm.open_value + forecast.open_value;
  const firmCoveragePct = totalSchedules ? (firm.schedules / totalSchedules) * 100 : 0;
  const firmValuePct    = totalValue ? (firm.total_value / totalValue) * 100 : 0;

  const fmtQty = (n: number) => Math.round(n).toLocaleString();

  // Stacked horizontal-bar series — visualises the firm/forecast split
  // across counts, qty and value on the same chart.
  const splitChart = [
    { dim: 'Schedules',   firm: firm.schedules,   forecast: forecast.schedules,
      firmLabel: fmtQty(firm.schedules), forecastLabel: fmtQty(forecast.schedules) },
    { dim: 'Open Qty',    firm: firm.open_qty,    forecast: forecast.open_qty,
      firmLabel: fmtQty(firm.open_qty),  forecastLabel: fmtQty(forecast.open_qty) },
    { dim: 'Open Value',  firm: firm.open_value,  forecast: forecast.open_value,
      firmLabel: formatCurrency(firm.open_value), forecastLabel: formatCurrency(forecast.open_value) },
  ];

  const valueDonut = [
    { name: 'Firm window', value: firm.open_value,     color: PALETTE.firm },
    { name: 'Forecast',    value: forecast.open_value, color: PALETTE.forecast },
  ];

  return (
    <div className="space-y-6">
      {/* Section frames — same visual language as other sub-tabs */}
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Firm Window" accent="green" subtitle={`next ${default_firm_days}d (per-part*)`}>
          <div className="grid grid-cols-3 gap-3 items-start">
            <KPICell
              label="Schedules"
              value={<span className="text-2xl font-semibold tabular-nums">{fmtQty(firm.schedules)}</span>}
            />
            <KPICell
              label="Open Qty"
              value={<span className="text-2xl font-semibold tabular-nums">{fmtQty(firm.open_qty)}</span>}
              foot={`of ${fmtQty(firm.total_qty)}`}
            />
            <KPICell
              label="Open Value"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(firm.open_value)}</span>}
              foot={`${formatCurrency(firm.total_value)} total`}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Forecast — Beyond Firm" accent="blue" subtitle="future commitments">
          <div className="grid grid-cols-3 gap-3 items-start">
            <KPICell
              label="Schedules"
              value={<span className="text-2xl font-semibold tabular-nums">{fmtQty(forecast.schedules)}</span>}
            />
            <KPICell
              label="Open Qty"
              value={<span className="text-2xl font-semibold tabular-nums">{fmtQty(forecast.open_qty)}</span>}
              foot={`of ${fmtQty(forecast.total_qty)}`}
            />
            <KPICell
              label="Open Value"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(forecast.open_value)}</span>}
              foot={`${formatCurrency(forecast.total_value)} total`}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Pipeline Health" accent="red" subtitle="as of today">
          <div className="grid grid-cols-3 gap-3 items-start">
            <KPICell
              label="Coverage"
              value={<CircularProgress percentage={firmCoveragePct} size={68} strokeWidth={6} color={PALETTE.firm} />}
              foot="firm % of forward"
            />
            <KPICell
              label="Total Forward"
              value={<span className="text-2xl font-semibold tabular-nums">{fmtQty(totalSchedules)}</span>}
              foot="schedules"
            />
            <KPICell
              label="Total Open"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(totalOpenValue)}</span>}
              foot={`${fmtQty(totalOpenQty)} qty`}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Visual split — stacked horizontal bars across three dimensions */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Firm vs Forecast — Split View</CardTitle>
          <p className="text-[11px] text-muted-foreground">
            Each row shows how the forward pipeline divides between the firm window and beyond
          </p>
        </CardHeader>
        <CardContent>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={splitChart} layout="vertical" margin={{ top: 5, right: 60, bottom: 0, left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="dim" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} width={100} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={(value, _name, item) => {
                    const v = Number(value) || 0;
                    const payload = (item as { payload?: { dim?: string } } | undefined)?.payload;
                    if (payload?.dim === 'Open Value') return formatCurrency(v);
                    return fmtQty(v);
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                <Bar dataKey="firm"     stackId="s" fill={PALETTE.firm}     name="Firm window" radius={[0, 0, 0, 0]} />
                <Bar dataKey="forecast" stackId="s" fill={PALETTE.forecast} name="Forecast"    radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Open Value Mix</CardTitle>
            <p className="text-[11px] text-muted-foreground">
              Of {formatCurrency(totalOpenValue)} outstanding, {Math.round((firm.open_value / totalOpenValue) * 100 || 0)}% sits in the firm window
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={valueDonut}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={56}
                    outerRadius={84}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {valueDonut.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Pipeline at a Glance</CardTitle>
            <p className="text-[11px] text-muted-foreground">All forward-dated, real schedules (excl. line 0)</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-2">
              <div>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Forward Schedules</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{totalSchedules.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Total Qty Committed</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{fmtQty(totalQty)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Open Qty</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{fmtQty(totalOpenQty)}</p>
                <p className="text-[10px] text-muted-foreground mt-1">balance still owed</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Total Pipeline Value</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{formatCurrency(totalValue)}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{Math.round(firmValuePct)}% in firm window</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-[10px] text-muted-foreground">
        *Firm window is per-part — derived from <code>PartScheduleEntry.schedule_firm</code>. The
        {' '}<strong>{default_firm_days}-day</strong> default applies when a part has no entry.
      </p>
    </div>
  );
}
